import React from "react"

// Liquid-compatible filters, applied left to right: {{ headword | downcase }}. Explicit casing beats
// guessing from sentence position (proper nouns like "Gaeilge" stay capitalized mid-sentence).
const FILTERS: Record<string, (value: string) => string> = {
  downcase: (value) => value.toLowerCase(),
  upcase: (value) => value.toUpperCase(),
  capitalize: (value) => value.charAt(0).toUpperCase() + value.slice(1),
}

const applyFilters = (value: string, filterExpression: string): string =>
  filterExpression
    .split("|")
    .map((name) => name.trim())
    .filter(Boolean)
    .reduce(
      (current, name) => (FILTERS[name] ? FILTERS[name](current) : current),
      value
    )

/**
 * Resolves Liquid-style `{{ key | filter }}` references (and, for examples, the swung dash `~`)
 * against a context into the `[bracket]` highlight form used across the app (addDynamicTags). This
 * lets any string reference the headword and get the text-highlight effect, not just the tilde.
 *
 * A lightweight subset: variable interpolation plus a few casing filters (downcase, upcase,
 * capitalize), not the full Liquid engine.
 */
export const resolveTemplate = (
  text: string,
  context: Record<string, string | undefined> = {},
  options: { swungDash?: boolean; capitalizeValues?: boolean } = {}
): string => {
  if (!text) return text

  // Proper-noun senses (names, places, brands) capitalize their substituted forms automatically, so
  // templates just say {{ name }} instead of hand-writing {{ name | capitalize }} everywhere. Only the
  // interpolated value is capitalized, never the surrounding literal text ("Is mise Saoirse").
  const cap = (value: string) =>
    options.capitalizeValues ? FILTERS.capitalize(value) : value

  let out = text.replace(
    /\{\{\s*([\w.]+)\s*((?:\|\s*\w+\s*)*)\}\}/g,
    (_match, key, filters) => {
      const value = context[key]
      if (value == null || value === "") return ""
      return `[${cap(applyFilters(value, filters))}]`
    }
  )

  // The tilde is an example-only convention (the swung dash for the headword); it isn't resolved in
  // free text like the entry content, where a literal "~" can occur.
  if (options.swungDash && context.headword) {
    out = out.replace(/~/g, `[${cap(context.headword)}]`)
  }
  return out
}

/**
 * Renders `[bracket]` highlight markers as React spans. The safe, node-based counterpart of
 * addDynamicTags, for places that render React rather than an HTML string.
 */
export const renderHighlighted = (text: string): React.ReactNode => {
  if (!text) return text

  return text.split(/(\[[^\]]+\])/g).map((part, index) => {
    const match = part.match(/^\[([^\]]+)\]$/)
    if (match) {
      return (
        <span key={index} className="text-highlight px-1 py-0.5 rounded">
          {match[1]}
        </span>
      )
    }
    return <React.Fragment key={index}>{part}</React.Fragment>
  })
}

// The first {{ ... }} key that isn't the base word (name/headword). Identifies the form an example
// demonstrates, e.g. "uisce {{ genitive_singular }}" -> "genitive_singular".
export const firstFormReference = (text: string): string | null => {
  const references = [...text.matchAll(/\{\{\s*([\w.]+)/g)].map(
    (match) => match[1]
  )
  return references.find((key) => key !== "name" && key !== "headword") ?? null
}

/** Resolve a template and render it as highlighted React nodes. */
export const renderTemplate = (
  text: string,
  context: Record<string, string | undefined> = {},
  options: { swungDash?: boolean; capitalizeValues?: boolean } = {}
): React.ReactNode => renderHighlighted(resolveTemplate(text, context, options))
